import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Popover, Select, Typography } from 'antd';
import { LeftOutlined, ReadOutlined, RightOutlined } from '@ant-design/icons';
import { BooksService } from 'core/services';
import { useLanguage, useGetBibleUrlParams } from 'core/hooks';
import { IBibleBook, TESTAMENTS } from 'types';
import { APP_LANGUAGES_ENUM, TRANSLATIONS_IDS_MAP } from 'config/constants';

import { useReadingPageData } from '../useReadingPageData';

import { NOTES_VALUE } from './TranslationsSelector';

const { Text } = Typography;
interface ISelectOptionGroup {
  label: string;
  options: { value: number; label: string }[];
}

function BookNavigation() {
  const navigate = useNavigate();
  const { book, chapter, verse, translations } = useGetBibleUrlParams();
  const { currentLanguage, langShortcut } = useLanguage();
  const [booksList, setBooksList] = useState<IBibleBook[]>([]);
  const { versesCount } = useReadingPageData();
  const translationId = TRANSLATIONS_IDS_MAP[langShortcut];

  useEffect(() => {
    if (!translationId) return;
    BooksService.getBooksListByTranslationId(translationId)
      .then(data => setBooksList(data))
      .catch(() => setBooksList([]));
  }, [translationId]);

  const booksSorted = useMemo(
    () => [...booksList].sort((a, b) => a.number - b.number),
    [booksList],
  );

  const currentBook = useMemo(() => {
    if (langShortcut === APP_LANGUAGES_ENUM.ku && book && book < 40) {
      return booksList.find(b => b.number === 40);
    }

    return booksList.find(b => b.number === book);
  }, [booksList, langShortcut, book]);

  const currentBookIndex = useMemo(
    () => (book !== undefined ? booksSorted.findIndex(b => b.number === book) : -1),
    [booksSorted, book],
  );

  const prevBook = currentBookIndex > 0 ? booksSorted[currentBookIndex - 1] : null;
  const nextBook = currentBookIndex >= 0 && currentBookIndex < booksSorted.length - 1
    ? booksSorted[currentBookIndex + 1]
    : null;

  const bookOptions = useMemo<ISelectOptionGroup[]>(() => {
    const oldTestament = {
      label: currentLanguage.old_testament ?? 'Old Testament',
      options: booksList
        .filter(b => b.testament === TESTAMENTS.OLD)
        .map(b => ({ value: b.number, label: b.title })),
    };
    const newTestament = {
      label: currentLanguage.new_testament ?? 'New Testament',
      options: booksList
        .filter(b => b.testament === TESTAMENTS.NEW)
        .map(b => ({ value: b.number, label: b.title })),
    };
    return [oldTestament, newTestament].filter(g => g.options.length > 0);
  }, [booksList, currentLanguage.old_testament, currentLanguage.new_testament]);

  const handleBookSelect = (bookNumber: number) => {
    if (!translations) return;
    navigate(`/bible/${bookNumber}/1/0/${translations}`);
  };

  const handleChapterSelect = (chapterNumber: number) => {
    if (!book || !translations) return;
    const verseParam = verse ?? '0';
    navigate(`/bible/${book}/${chapterNumber}/${verseParam}/${translations}`);
  };

  const handleVerseSelect = (verseNumber: number) => {
    if (!book || !translations) return;
    navigate(`/bible/${book}/${chapter}/${verseNumber}/${translations}`, { state: { highlight: true } });
  };

  const handlePrevChapter = () => {
    if (!translations || chapter === undefined) return;
    if (chapter > 1) {
      handleChapterSelect(chapter - 1);

      return;
    }

    if (prevBook) {
      navigate(`/bible/${prevBook.number}/${prevBook.chapters_count}/0/${translations}`);
    }
  };

  const handleNextChapter = () => {
    if (!translations || chapter === undefined || !currentBook) return;
    if (chapter < currentBook.chapters_count) {
      handleChapterSelect(chapter + 1);

      return;
    }

    if (nextBook) {
      navigate(`/bible/${nextBook.number}/1/0/${translations}`);
    }
  };

  useEffect(() => {
    const translationsIds = translations?.split(',').filter(part => part !== NOTES_VALUE).map(Number).filter(n => !Number.isNaN(n));

    const isOldTestament = book < 40;
    const firstTranslationId = translationsIds?.[0];
    if (isOldTestament && translationsIds?.length === 1 && firstTranslationId === 3) {
      navigate(`/bible/${book}/${chapter}/${verse}/${translations?.includes(NOTES_VALUE) ? `1,${NOTES_VALUE}` : '1'}`);
    }
  }, [book, chapter, navigate, translations, verse]);

  if (!book || !translations || !currentBook) {
    return null;
  }

  const chapterCount = currentBook.chapters_count;
  const chapters = Array.from({ length: chapterCount }, (_, i) => i + 1);
  const verses = Array.from({ length: versesCount }, (_, i) => i + 1);

  const popoverContent = (
    <div className="bible-v2-header__book-nav-popover">
      <div className="bible-v2-header__book-nav-section">
        <label>{currentLanguage.selectBook}</label>
        <Select
          showSearch
          value={book}
          optionFilterProp="label"
          options={bookOptions}
          onChange={handleBookSelect}
          filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
          style={{ width: '100%', marginBottom: 8 }}
        />
      </div>
      <div className="bible-v2-header__book-nav-section__flex">
        <div className="bible-v2-header__book-nav-section__flex-item">
          <Text className="label">{currentLanguage.selectChapter}</Text>
          <div className="bible-v2-header__book-nav-chapters">
            {chapters.map(num => (
              <Button
                key={num}
                type={chapter === num ? 'primary' : 'default'}
                size="small"
                onClick={() => handleChapterSelect(num)}
              >
                {num}
              </Button>
            ))}
          </div>
        </div>
        <div className="bible-v2-header__book-nav-section__flex-item">
          <Text className="label">{currentLanguage.selectVerse}</Text>
          <div className="bible-v2-header__book-nav-verses">
            {verses.map(num => (
              <Button key={num} type="default" size="small" onClick={() => handleVerseSelect(num)}>
                {num}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const canGoPrev = chapter !== undefined && (chapter > 1 || prevBook !== undefined);
  const canGoNext = chapter !== undefined
    && (chapter < currentBook.chapters_count || nextBook !== undefined);

  return (
    <div className="bible-v2-header__book-nav">
      <Button
        type="text"
        icon={<LeftOutlined />}
        onClick={handlePrevChapter}
        disabled={!canGoPrev}
        aria-label="Previous chapter"
        className="bible-v2-header__book-nav-arrow"
      />
      <Popover
        title={currentBook.title}
        content={popoverContent}
        trigger="click"
        placement="bottomLeft"
      >
        <Button icon={<ReadOutlined />} className="bible-v2-header__book-nav-btn">
          {currentBook.title}
          {' '}
          {chapter}
        </Button>
      </Popover>
      <Button
        type="text"
        icon={<RightOutlined />}
        onClick={handleNextChapter}
        disabled={!canGoNext}
        aria-label="Next chapter"
        className="bible-v2-header__book-nav-arrow"
      />
    </div>
  );
}

export default BookNavigation;
